import { ROOT_DB_URL } from '../../core/configs/FirestoreConfigs';
import OenoviewScopes from '../../core/models/PixagriScopes';
import { firestoreDataService } from '../../core/services/FirestoreService';
import { logger } from '../../core/services/LogService';
import StatisticIndicatorLegendColorCase from '../../statistic/models/StatisticIndicatorLegendColorCase';
import StatisticIndicatorModel from '../../statistic/models/StatisticIndicatorModel';
import StatisticIndicatorType from '../../statistic/models/StatisticIndicatorType';
import StatisticIndicatorValueType from '../../statistic/models/StatisticIndicatorValueType';
import { buildIndicatorLegend } from '../../statistic/services/LegendService';
import { OrganizationContractModel } from '../../subscriptions/models/OrganizationContractModel';
import ProcessingModel from '../models/ProcessingModel';
import ProcessingType from '../models/ProcessingType';

export const DEFAULT_PRECISION_FACTOR = 1000;
class SharedProcessingsService {
    getProcessingsCollectionPath(): string {
        return `${ROOT_DB_URL}/agri/${OenoviewScopes.PROCESSINGS}`;
    }

    getProcessingDocPath(processingType: ProcessingType): string {
        const path = `${this.getProcessingsCollectionPath()}/${processingType}`
        return path;

    }

    async buildLegendsIfMissing(processings: {
        [key in ProcessingType]?: ProcessingModel;
    }): Promise<void> {
        // Legends
        const legendBuilders: Promise<{
            indicatorType: StatisticIndicatorType;
            legends: StatisticIndicatorLegendColorCase[];
        }>[] = [];

        for (const processingKey in processings) {
            const processingType = processingKey as ProcessingType;
            const processingConfig = processings[processingType];
            if (processingConfig) {
                for (const indicatorKey in processingConfig.indicators) {
                    const indicatorType = indicatorKey as StatisticIndicatorType;
                    const processingIndicator =
                        processingConfig.indicators[indicatorType];
                    if (!processingIndicator?.legend && processingIndicator?.legend_url) {
                        logger.info(
                            `Legend does not exist, building it from ${processingIndicator?.legend_url}`,
                        );
                        legendBuilders.push(buildIndicatorLegend(processingIndicator));
                    }
                }
            }
        }
        await Promise.all(legendBuilders);
    }



    async getProcessingsConfig(): Promise<{
        [key in ProcessingType]?: ProcessingModel;
    }> {
        const processingsCollectionPath = this.getProcessingsCollectionPath();
        const config: { [key in ProcessingType]?: ProcessingModel } = {};
        const docSnap = await firestoreDataService.getColFromFirestore(
            processingsCollectionPath,
        );

        logger.debug(`fetch ${docSnap.size} doc of processing config`);

        if (docSnap) {
            docSnap.forEach((doc) => {
                if (doc) {
                    const processingConfig = doc.data() as ProcessingModel;
                    config[doc.id as ProcessingType] = processingConfig;
                }
            });
        }

        await this.buildLegendsIfMissing(config);
        return config;
    }

    getDefaultIndicator(config: { [key in ProcessingType]?: ProcessingModel } | null, lastCampaign: number, subscriptions: { [key: number]: { [key in ProcessingType]?: OrganizationContractModel } }, lastIndicatorUsed: StatisticIndicatorType | null): StatisticIndicatorType | null {
        let defaultIndicator: StatisticIndicatorType | null = null
        if (!config) {
            return defaultIndicator
        }
        const supportedProcessingTypeAtLastCampaign: ProcessingType[] = this.getSupportedProcessingTypeForCampaignYear(subscriptions, lastCampaign) ?? Object.keys(config) as ProcessingType[];

        if (supportedProcessingTypeAtLastCampaign?.length) {
            let isFound = false
            supportedProcessingTypeAtLastCampaign.forEach((processingType) => {
                const model = config[processingType]
                if (model && model.indicators) {
                    const indicators = Object.keys(model.indicators) as StatisticIndicatorType[]
                    if (indicators.includes(lastIndicatorUsed as StatisticIndicatorType)) {
                        defaultIndicator = lastIndicatorUsed as StatisticIndicatorType
                        isFound = true
                    }
                }
            })
            if (isFound) {
                logger.info("default indicator found from local settings is still valid:", defaultIndicator)
                defaultIndicator = lastIndicatorUsed as StatisticIndicatorType
                return defaultIndicator
            } else {
                const firstProcessingTypeLastCampaign = supportedProcessingTypeAtLastCampaign.find(service => Object.keys(config).includes(service));
                if (config && firstProcessingTypeLastCampaign && config[firstProcessingTypeLastCampaign]?.indicators) {
                    const indicators = config[firstProcessingTypeLastCampaign]?.indicators
                    const indicatorsList = indicators ? Object.values(indicators) : null
                    defaultIndicator = indicatorsList && indicatorsList.length ? indicatorsList.sort((a, b) => { return (a.index ? a.index : 0) - (b.index ? b.index : 0) })[0].ref as StatisticIndicatorType : null
                } else {
                    logger.error("no config for", firstProcessingTypeLastCampaign, "found in ", config)
                }
            }
        } else {
            logger.error("no supported Processing Type At Last Campaign")
        }


        return defaultIndicator

    }

    isRightLegendColorCase(
        legend: StatisticIndicatorLegendColorCase,
        value: number | string,
        valueType: StatisticIndicatorValueType,
    ): boolean | undefined {
        const precisionFactor = legend.precision_factor ?? DEFAULT_PRECISION_FACTOR;
        const roundedValue = Math.round((value as number) * precisionFactor);
        switch (valueType) {
            case StatisticIndicatorValueType.NUMBER:
            case StatisticIndicatorValueType.PERCENTAGE:
                if ((legend.min || legend.min === 0) && legend.max) {
                    if (legend.max_inclusive) {
                        return (
                            Math.round(legend.min * precisionFactor) <= roundedValue &&
                            Math.round(legend.max * precisionFactor) >= roundedValue
                        );
                    } else {
                        return (
                            Math.round(legend.min * precisionFactor) <= roundedValue &&
                            Math.round(legend.max * precisionFactor) > roundedValue
                        );
                    }
                }
                return undefined;
            case StatisticIndicatorValueType.LEVEL:
                return (value as string) == legend.level;
            default:
                return undefined;
        }
    }

    getProcessingName(config?: ProcessingModel | null, local?: string | null) {
        if (!config || !local) {
            logger.debug(`Cannot get processing translation `);
            return null;
        }
        if (config.names[local]) {
            return config.names[local];
        }
        logger.warn(
            `Missing translation ${config.ref} for local ${local} => return ref instead`,
        );
        return config.ref;
    }
    getIndicatorName(
        config?: StatisticIndicatorModel | null,
        local?: string | null,
    ) {
        if (!config || !local) {
            logger.debug(`Cannot get Indicator translation `);
            return null;
        }

        if (config.names[local]) {
            return config.names[local];
        }
        logger.warn(
            `Missing translation ${config.ref} for local ${local} => return ref instead`,
        );
        return config.ref;
    }

    retrieveIndicatorNameFromFullConfig(
        config: { [key in ProcessingType]?: ProcessingModel } | null,
        indicator: StatisticIndicatorType,
        lang: string | null,
    ) {
        if (!config || !lang) {
            return null;
        }
        const processingModelList = Object.values(config);
        const indicatorModel =
            processingModelList.find(
                (model) =>
                    model &&
                    model.indicators &&
                    Object.keys(model.indicators).includes(indicator),
            )?.indicators || null;
        if (!indicatorModel || !indicatorModel[indicator]) {
            return null;
        }
        return indicatorModel[indicator]?.names[lang] || null;
    }
    retrieveProcessingRefFromFullConfig(
        config: { [key in ProcessingType]?: ProcessingModel } | null,
        processing: ProcessingType,
        lang: string | null,
    ) {
        if (!config || !lang) {
            return null;
        }
        const processingModelList = config && Object.values(config);

        const processingService =
            processingModelList && processingModelList.find(
                (model) =>
                    model &&
                    model.descriptions &&
                    Object.values(model.descriptions).includes(processing),
            )?.ref || null;
        if (!processingService) {
            return null;
        }
        return processingService
    }
    getIndicatorLabelName(
        config?: StatisticIndicatorModel,
        local?: string,
    ): { high_label: string; low_label: string } {
        const labels = {
            high_label: 'high',
            low_label: 'low',
        };
        if (config?.display_min_max_as_label) {
            return labels
        }
        if (config && local) {
            if (config.high_label && config.high_label[local]) {
                labels.high_label = config.high_label[local];
            } else {
                logger.warn(
                    `Missing translation high_label of ${config.ref} for local ${local} => return "high" instead`,
                );
            }
            if (config.low_label && config.low_label[local]) {
                labels.low_label = config.low_label[local];
            } else {
                logger.warn(
                    `Missing translation low_label of ${config.ref} for local ${local} => return "low" instead`,
                );
            }
        } else {
            logger.error(`Cannot get labels translation `);
        }

        return labels;
    }

    getSupportedProcessingTypeForCampaignYear(
        data: {
            [campaignYear: number]: {
                [processingType in ProcessingType]?: OrganizationContractModel;
            };
        },
        campaign_year: number,
    ): ProcessingType[] {
        const subscriptionsOfYear = data[campaign_year];
        if (subscriptionsOfYear) {
            const processingTypes = Object.keys(
                subscriptionsOfYear,
            ) as ProcessingType[];
            return processingTypes;
        }

        return [ProcessingType.IRRIGATION, ProcessingType.VEGETATION];
    }

    getSupportedIndicators(
        supportedProcessingType: ProcessingType[],
        config: { [key in ProcessingType]?: ProcessingModel },
    ) {
        const newSupportedIndicator: StatisticIndicatorType[] = [];

        supportedProcessingType.forEach((processingType) => {
            const configForProcessing = config[processingType] || null;
            if (configForProcessing) {
                const indicators = Object.keys(
                    configForProcessing?.indicators ?? {},
                ) as StatisticIndicatorType[];
                indicators.forEach((indicator) => {
                    if (!newSupportedIndicator.find((indic) => indic === indicator)) {
                        newSupportedIndicator.push(indicator);
                    }
                });
            }
        });

        return newSupportedIndicator;
    }

    getSupportedIndicatorsForCampaignYear(
        data: {
            [campaignYear: number]: {
                [processingType in ProcessingType]?: OrganizationContractModel;
            };
        } | null,
        campaign_year: number | null,
        config: { [key in ProcessingType]?: ProcessingModel } | null,
    ) {
        if (!data || !campaign_year || !config) {
            return [] as StatisticIndicatorType[];
        }
        const supportedProcessingType =
            this.getSupportedProcessingTypeForCampaignYear(data, campaign_year);

        return this.getSupportedIndicators(supportedProcessingType, config);
    }

    getProcessingOfIndicator(
        indicator: StatisticIndicatorType,
        config: { [key in ProcessingType]?: ProcessingModel } | null,
    ): ProcessingType | null {
        let processingType: ProcessingType | null = null;
        if (config) {
            const processingFound = Object.keys(config).find((processing) => {
                const processingModel = config[processing as ProcessingType];
                if (processingModel && processingModel.indicators[indicator]) {
                    return true;
                }
                return false
            });
            if (!processingFound) {
                logger.debug(
                    `Processing of indicator ${indicator} not found in config`,
                );
            } else {
                processingType = processingFound as ProcessingType;
            }
        }
        return processingType;
    }

    getIndicatorOfProcessing(processing: ProcessingType, config?: { [key in ProcessingType]?: ProcessingModel } | null): StatisticIndicatorType[] {
        let statisticIndicatorList: StatisticIndicatorType[] = [];

        if (config) {
            const processingModel = config[processing]
            if (processingModel && processingModel.indicators) {
                const statisticsSorted = Object.values(processingModel.indicators).sort((a, b) => (a.index ?? Infinity) - (b.index ?? Infinity))
                const statisticsList = statisticsSorted.map(statModel => statModel.ref)
                statisticIndicatorList = statisticsList
            }
        }
        return statisticIndicatorList
    }
}
const sharedProcessingsService = new SharedProcessingsService();
export default sharedProcessingsService;
